import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Trash2, Copy, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";

export default function ApiKeys() {
  const t = useT();
  const [keys, setKeys] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [name, setName] = useState("");
  const [revealed, setRevealed] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/api-keys"); setKeys(data.keys); setError(""); }
    catch (e) { setError(e?.response?.data?.detail || t("apikeys.upgrade")); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/api-keys", { name });
      setRevealed(data); setName(""); setShowNew(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => { await api.delete(`/api-keys/${id}`); load(); };

  if (error) {
    return (
      <div className="p-12 max-w-2xl">
        <div className="overline mb-3">{t("apikeys.overline")}</div>
        <h1 className="font-display font-black text-3xl tracking-tighter mb-3">{t("apikeys.titleSub")}</h1>
        <div className="surface p-6 text-white/60 text-sm">
          {t("apikeys.upgrade")} <a href="/app/billing" className="text-primary hover:underline">→ Billing</a>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 lg:p-12 max-w-5xl">
      <div className="overline mb-3">{t("apikeys.overline")}</div>
      <div className="flex justify-between items-end mb-10">
        <h1 className="font-display font-black text-4xl tracking-tighter">{t("apikeys.title")}</h1>
        <button onClick={() => setShowNew(!showNew)} className="btn-primary text-sm py-2 px-4" data-testid="apikeys-new-btn">
          <Plus className="w-4 h-4" /> {t("apikeys.generate")}
        </button>
      </div>

      {revealed && (
        <div className="surface p-5 mb-6 border-primary" data-testid="apikeys-reveal">
          <div className="overline text-primary mb-2">{t("apikeys.warning")}</div>
          <div className="flex gap-2 items-center font-mono text-xs">
            <code className="flex-1 bg-black/40 p-3 break-all">{revealed.key}</code>
            <button onClick={() => { navigator.clipboard.writeText(revealed.key); toast.success("Copied"); }} className="btn-ghost py-2 px-3">
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {showNew && (
        <form onSubmit={create} className="surface p-5 mb-6 flex gap-3 items-end" data-testid="apikeys-new-form">
          <div className="flex-1">
            <label className="overline block mb-1">{t("apikeys.keyName")}</label>
            <input required value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. ci-pipeline"
              className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono" data-testid="apikeys-name-input" />
          </div>
          <button className="btn-primary" data-testid="apikeys-create-btn">{t("apikeys.generate")}</button>
        </form>
      )}

      <div className="surface">
        {keys.length === 0 ? (
          <div className="p-12 text-center text-white/40 text-sm">
            <KeyRound className="w-8 h-8 mx-auto mb-3 opacity-30" />
            {t("apikeys.noKeys")}
          </div>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {keys.map((k) => (
                <tr key={k.id} className="border-b border-white/5" data-testid={`apikeys-row-${k.id}`}>
                  <td className="p-4 font-medium">{k.name}</td>
                  <td className="p-4 font-mono text-xs text-white/50">{k.key_prefix}…</td>
                  <td className="p-4 font-mono text-xs text-white/40">{new Date(k.created_at).toLocaleDateString()}</td>
                  <td className="p-4 text-right">
                    <button onClick={() => del(k.id)} className="text-xs text-red-400 hover:underline inline-flex items-center gap-1">
                      <Trash2 className="w-3 h-3" /> {t("common.revoke").toLowerCase()}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
